import React, { useRef, useEffect, memo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import InfoIcon from "@mui/icons-material/Info";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    marginBottom: "16px",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    "& .MuiTableCell-head": {
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableRow: {
    "&:nth-of-type(odd)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  addButton: {
    marginTop: "16px",
  },
  textField: {
    "& .MuiInputBase-input": {
      padding: "8px 12px",
    },
  },
  disabledTextField: {
    "& .MuiInputBase-input": {
      padding: "8px 12px",
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
      cursor: "default",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f8f9fa",
      border: "1px solid #e9ecef",
      borderRadius: "6px",
      "&.Mui-disabled": {
        backgroundColor: "#f8f9fa",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      },
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "32px",
    color: theme.palette.text.secondary,
  },
  headerWithTooltip: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  dragHandle: {
    cursor: "grab",
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.text.primary,
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
  dragging: {
    opacity: 0.5,
    backgroundColor: "#e3f2fd",
  },
  dropTarget: {
    backgroundColor: "#f0f7ff",
    borderTop: "2px solid #2196f3",
  },
}));

// Separate memoized component to prevent focus loss
const DraggableRow = memo(({ row, index, handleFieldChange, handleDeleteRow, getFieldError, handleReorder, styles, dragStateRef, isDragEnabled = true }: {
  row: any;
  index: number;
  handleFieldChange: (index: number, field: string, value: any) => void;
  handleDeleteRow: (index: number) => void;
  getFieldError: (index: number, field: string) => string | undefined;
  handleReorder: (fromIndex: number, toIndex: number) => void;
  styles: any;
  dragStateRef: any;
  isDragEnabled?: boolean;
}) => {
  const rowRef = useRef(null);

  useEffect(() => {
    const element = rowRef.current;
    if (!element || !isDragEnabled) return; // Exit early if drag is disabled

    const dragCleanup = draggable({
      element,
      getInitialData: () => ({ index }),
      onDragStart: () => {
        dragStateRef.current = {
          isDragging: true,
          draggedIndex: index,
          dropTargetIndex: null,
        };
        element.classList.add(styles.dragging);
      },
      onDrop: () => {
        element.classList.remove(styles.dragging);
        dragStateRef.current = {
          isDragging: false,
          draggedIndex: null,
          dropTargetIndex: null,
        };
      },
    });

    const dropCleanup = dropTargetForElements({
      element,
      onDragEnter: () => {
        if (dragStateRef.current.isDragging && dragStateRef.current.draggedIndex !== index) {
          element.classList.add(styles.dropTarget);
          dragStateRef.current.dropTargetIndex = index;
        }
      },
      onDragLeave: () => {
        element.classList.remove(styles.dropTarget);
        if (dragStateRef.current.dropTargetIndex === index) {
          dragStateRef.current.dropTargetIndex = null;
        }
      },
      onDrop: ({ source }) => {
        element.classList.remove(styles.dropTarget);
        const sourceIndex = source.data.index;
        if (typeof sourceIndex === 'number' && sourceIndex !== index) {
          handleReorder(sourceIndex, index);
        }
      },
    });

    return () => {
      dragCleanup();
      dropCleanup();
    };
  }, [index, handleReorder, styles, dragStateRef, isDragEnabled]); // Add isDragEnabled to dependencies

  return (
    <TableRow ref={rowRef} className={styles.tableRow}>
      {isDragEnabled && (
        <TableCell>
          <IconButton className={styles.dragHandle} size="small">
            <DragIndicatorIcon />
          </IconButton>
        </TableCell>
      )}
      <TableCell>
        <TextField
          type="number"
          value={row.minOvr}
          onChange={(e) => handleFieldChange(index, 'minOvr', e.target.value)}
          size="small"
          className={styles.disabledTextField}
          inputProps={{ min: 0, max: 99 }}
          error={!!getFieldError(index, 'minOvr')}
          disabled
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={row.maxOvr}
          onChange={(e) => handleFieldChange(index, 'maxOvr', e.target.value)}
          size="small"
          className={styles.disabledTextField}
          inputProps={{ min: 0, max: 99 }}
          error={!!getFieldError(index, 'maxOvr')}
          disabled
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={row.staminaLossMin}
          onChange={(e) => handleFieldChange(index, 'staminaLossMin', e.target.value)}
          size="small"
          className={styles.textField}
          inputProps={{ min: 0, max: 100 }}
          error={!!getFieldError(index, 'staminaLoss')}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={row.staminaLossMax}
          onChange={(e) => handleFieldChange(index, 'staminaLossMax', e.target.value)}
          size="small"
          className={styles.textField}
          inputProps={{ min: 0, max: 100 }}
          error={!!getFieldError(index, 'staminaLoss')}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </TableCell>
      {/* <TableCell align="center">
        <Tooltip title="Delete this rule">
          <IconButton
            size="small"
            onClick={() => handleDeleteRow(index)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell> */}
    </TableRow>
  );
});

const StaminaReductionConfig = ({ data = [], onChange, errors = [], isDragEnabled = true }: any) => {
  const styles = useStyles();
  const dragStateRef = useRef({
    isDragging: false,
    draggedIndex: null,
    dropTargetIndex: null,
  });

  // const handleAddRow = () => {
  //   const newRow = {
  //     minOvr: 0,
  //     maxOvr: 0,
  //     staminaLossMin: 0,
  //     staminaLossMax: 0,
  //   };
  //   onChange([...data, newRow]);
  // };

  const handleDeleteRow = (index: any) => {
    const newData = data.filter((_, i) => i !== index);
    onChange(newData);
  };

  const handleFieldChange = (index, field, value) => {
    const newData = [...data];

    // Allow empty string for better UX, convert to number only when not empty
    let processedValue;
    if (value === '' || value === null || value === undefined) {
      processedValue = '';
    } else {
      const numericValue = parseInt(value);
      processedValue = isNaN(numericValue) ? 0 : numericValue;
    }

    newData[index] = {
      ...newData[index],
      [field]: processedValue,
    };
    onChange(newData);
  };

  const handleReorder = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const newData = [...data];
    const item = newData.splice(fromIndex, 1)[0];
    newData.splice(toIndex, 0, item);
    onChange(newData);
  };

  const getFieldError = (index: number, field: string) => {
    return errors.find(error =>
      error.includes(`Row ${index + 1}`) &&
      (field.includes('Ovr') ? error.includes('OVR') : error.includes('Stamina'))
    );
  };

  // Move DraggableRow outside to prevent recreating on every render
  // We'll create a separate component later

  if (data.length === 0) {
    return (
      <Box>
        <Paper className={styles.emptyState}>
          <Typography variant="h6" gutterBottom>
            No Stamina Reduction Rules Configured
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add rules to define stamina percentage reduction based on player OVR.
          </Typography>
        </Paper>
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          className={styles.addButton}
        >
          Add First Rule
        </Button> */}
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table size="small">
          <TableHead className={styles.tableHeader}>
            <TableRow>
              {isDragEnabled && <TableCell width="60px">Order</TableCell>}
              <TableCell>
                <Box className={styles.headerWithTooltip}>
                  Min OVR
                  <Tooltip title="Minimum OVR rating for this rule">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={styles.headerWithTooltip}>
                  Max OVR
                  <Tooltip title="Maximum OVR rating for this rule">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={styles.headerWithTooltip}>
                  Min Stamina Loss %
                  <Tooltip title="Minimum stamina percentage lost after a match">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Box className={styles.headerWithTooltip}>
                  Max Stamina Loss %
                  <Tooltip title="Maximum stamina percentage lost after a match">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </TableCell>
              {/* <TableCell align="center">Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <DraggableRow
                key={`row-${index}`}
                row={row}
                index={index}
                handleFieldChange={handleFieldChange}
                handleDeleteRow={handleDeleteRow}
                getFieldError={getFieldError}
                handleReorder={handleReorder}
                styles={styles}
                dragStateRef={dragStateRef}
                isDragEnabled={isDragEnabled}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddRow}
        className={styles.addButton}
      >
        Add New Rule
      </Button> */}

      {errors.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="error">
            Configuration Issues:
          </Typography>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2" color="error" component="li" sx={{ ml: 2 }}>
              {error}
            </Typography>
          ))}
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          <strong>Note:</strong> Ranges should not overlap. Players will lose stamina randomly between
          the min and max percentage values based on their OVR falling within the specified range.
        </Typography>
      </Box>
    </Box>
  );
};

export default StaminaReductionConfig;